// src/components/songEdit/versions/PendingVersionsList.tsx
import React, { useState } from 'react';
import { PendingVersion } from '../types/songEdit';
import { AddVersionModal, VersionFormData } from './AddVersionModal';
import { useConfirmModal } from '../../common/ConfirmModal';
import './VersionsList.css';

interface PendingVersionsListProps {
  pendingVersions: PendingVersion[];
  onAdd: (version: Omit<PendingVersion, 'tempId'>) => void;
  onUpdate: (tempId: string, updates: Partial<PendingVersion>) => void;
  onRemove: (tempId: string) => void;
  onSetDefault: (tempId: string) => void;
}

export const PendingVersionsList: React.FC<PendingVersionsListProps> = ({
  pendingVersions,
  onAdd,
  onUpdate,
  onRemove,
  onSetDefault
}) => {
  const { confirm, ConfirmModal } = useConfirmModal();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<PendingVersion | null>(null);

  const formatKey = (key: string | undefined) => {
    if (!key) return '-';
    return key.replace('KEY_', '').replace(/_/g, ' ');
  };

  const formatPerformanceType = (type: string | undefined) => {
    if (!type) return '-';
    const types: Record<string, string> = {
      'CONG': 'Congregation',
      'CHOIR': 'Choir',
      'GOSPEL': 'Gospel Choir',
      'CANTOR': 'Cantor',
      'SOLOIST': 'Soloist'
    };
    return types[type] || type;
  };

  const handleAddVersion = () => {
    setSelectedVersion(null);
    setIsModalOpen(true);
  };

  const handleEditVersion = (version: PendingVersion) => {
    setSelectedVersion(version);
    setIsModalOpen(true);
  };

  const handleDeleteVersion = (tempId: string) => {
    confirm({
      title: 'Remove Version',
      message: 'Are you sure you want to remove this version?',
      confirmText: 'Remove',
      variant: 'danger',
      onConfirm: () => {
        onRemove(tempId);
      }
    });
  };

  const handleSaveVersion = async (versionData: VersionFormData) => {
    if (selectedVersion) {
      // Update existing pending version
      onUpdate(selectedVersion.tempId, versionData);
    } else {
      // Add new pending version
      onAdd(versionData);
    }
    setIsModalOpen(false);
    setSelectedVersion(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedVersion(null);
  };

  // Convert PendingVersion to the format AddVersionModal expects
  const getExistingVersionForModal = () => {
    if (!selectedVersion) return null;
    return {
      version_id: 0, // Fake ID for pending versions
      song_id: 0,
      version_title: selectedVersion.version_title,
      alt_title: selectedVersion.alt_title || null,
      arranger_name: selectedVersion.arranger_name || null,
      arrangement_type: selectedVersion.arrangement_type || null,
      performance_type: selectedVersion.performance_type || null,
      key_signature: selectedVersion.key_signature || null,
      verses_included: selectedVersion.verses_included || null,
      video_link: selectedVersion.video_link || null,
      canonical_filename: null,
      file_path: null,
      file_extension: null,
      version_number: null,
      is_default: selectedVersion.is_default || false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  };

  return (
    <div className="versions-list">
      <div className="versions-list__header">
        <h3>Song Versions</h3>
        <button
          className="btn btn-primary btn-sm"
          onClick={handleAddVersion}
        >
          + Add Version
        </button>
      </div>

      <div className="alert alert-info" role="alert">
        <small>
          <strong>Note:</strong> Versions will be created when you save the song.
          Files can be added after the song is saved.
        </small>
      </div>

      {pendingVersions.length === 0 ? (
        <div className="versions-list__empty">
          <p>No versions added yet.</p>
          <p className="text-muted">Click "Add Version" to create the first version for this song.</p>
        </div>
      ) : (
        <div className="versions-list__table-container">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Version Title</th>
                <th>Arranger</th>
                <th>Performance Type</th>
                <th>Key</th>
                <th>Arrangement Type</th>
                <th>Default</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingVersions.map((version) => (
                <tr key={version.tempId} className={version.is_default ? 'table-primary' : ''}>
                  <td>
                    <strong>{version.version_title || version.alt_title || '-'}</strong>
                    {version.video_link && (
                      <a
                        href={version.video_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ms-2 text-primary"
                        title="Watch video"
                      >
                        🎥
                      </a>
                    )}
                  </td>
                  <td>{version.arranger_name || '-'}</td>
                  <td>{formatPerformanceType(version.performance_type)}</td>
                  <td>{formatKey(version.key_signature)}</td>
                  <td>{version.arrangement_type || '-'}</td>
                  <td>
                    {version.is_default ? (
                      <span className="badge bg-primary">Default</span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={() => onSetDefault(version.tempId)}
                        title="Set as default version"
                      >
                        Set Default
                      </button>
                    )}
                  </td>
                  <td>
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => handleEditVersion(version)}
                        title="Edit version"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => handleDeleteVersion(version.tempId)}
                        title="Delete version"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pendingVersions.length > 0 && (
        <div className="versions-list__footer">
          <small className="text-muted">
            Total versions: {pendingVersions.length} (pending)
          </small>
        </div>
      )}

      <AddVersionModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveVersion}
        songId={0} // Not used for pending versions
        existingVersion={getExistingVersionForModal()}
      />
      <ConfirmModal />
    </div>
  );
};
